import React from 'react'

import { sortBy } from 'lodash'

import {
  Paper,
  Box,
  Typography,
  Divider,
  CircularProgress,
} from '@material-ui/core'

import * as service from 'service'
import helpers from 'helpers'
import constants from './../../../../constants'
import useFetch from 'hooks/useFetch'
import useConfirmDialog from 'hooks/useConfirmDialog'
import useSnackbar from 'hooks/useSnackbar'
import GenericCollapse from './components/GenericCollapse'
import InvoiceCollapse from './components/InvoiceCollapse'
import DialogInvoicesEmails from './components/DialogInvoicesEmails'

const EmailsMain = () => {
  const confirmDialog = useConfirmDialog()
  const snackbar = useSnackbar()
  const [emails, setEmails] = React.useState([])
  const [email, setEmail] = React.useState(null)
  const [openEditEmail, setOpenEditEmail] = React.useState(false)

  const mappedEmails = React.useMemo(
    () => ({
      customer: helpers.emails.mapEmails(emails, constants.emails.CUSTOMER),
      contract: helpers.emails.mapEmails(emails, constants.emails.CONTRACT),
      contractPlan: helpers.emails.mapEmails(
        emails,
        constants.emails.CONTRACT_PLAN,
      ),
      invoice: helpers.emails.mapEmails(emails, constants.emails.INVOICE),
    }),
    [emails],
  )

  const { isLoading, response } = useFetch(service.recurrence.emails.get, {
    perPage: 1000,
  })

  React.useEffect(() => {
    if (!response) {
      return
    }

    setEmails(
      helpers.emails.hideManualActionEmails(sortBy(response.data, email => email.id)),
    )
  }, [response])

  const handleStatusChange = async (event, email) => {
    const checked = event.target.checked

    if (!checked) {
      confirmDialog.open({
        title: 'Inativar email',
        message: 'Deseja realmente inativar este envio de email?',
        cancelButton: 'Cancelar',
        confirmButton: 'Inativar',
        handleConfirm: e => updateEmail({ emailId: email.id, active: checked }),
      })

      return
    }

    updateEmail({ emailId: email.id, active: checked })
  }

  const handleClickEdit = email => {
    setOpenEditEmail(true)
    setEmail(email)
  }

  const handleSubmit = async email => {
    try {
      await updateEmail({
        emailId: email.id,
        variables: [
          {
            name: 'quantity_days_to_due_date',
            value: email.quantityDaysToDueDate,
          },
        ],
      })

      snackbar.open({
        message: 'Configuração de email alterada com sucesso!',
        variant: 'success',
      })
    } catch (error) {
      snackbar.open({
        message: 'Erro ao alterar configuração de email!',
        variant: 'error',
      })
    } finally {
      setOpenEditEmail(false)
    }
  }

  const handleClose = () => {
    setOpenEditEmail(false)
  }

  const updateEmail = async ({ emailId, ...data }) => {
    const updatedEmail = (await service.recurrence.emails.update({
      emails: [{ id: emailId, ...data }],
    })).data[0]

    setEmails(emails =>
      emails.map(email => {
        if (email.id !== updatedEmail.id) {
          return email
        }

        return updatedEmail
      }),
    )
  }

  return (
    <Paper>
      <Box
        p={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="h4">Emails</Typography>
      </Box>

      <Divider />

      {isLoading ? (
        <Box p={2} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </Box>
      ) : (
        <Box p={2}>
          <GenericCollapse
            emails={mappedEmails.customer}
            entityName="Clientes"
            onStatusChange={handleStatusChange}
          />
          <GenericCollapse
            emails={mappedEmails.contract}
            entityName="Assinaturas"
            onStatusChange={handleStatusChange}
          />
          <GenericCollapse
            emails={mappedEmails.contractPlan}
            entityName="Planos de Assinaturas"
            onStatusChange={handleStatusChange}
          />
          <InvoiceCollapse
            emails={mappedEmails.invoice}
            onStatusChange={handleStatusChange}
            onClickEdit={handleClickEdit}
          />
        </Box>
      )}

      {openEditEmail && (
        <DialogInvoicesEmails
          open={openEditEmail}
          email={email}
          onSubmit={handleSubmit}
          onClose={handleClose}
        />
      )}
    </Paper>
  )
}

export default EmailsMain
